import { z } from 'zod'

// ============================================================================
// Input
// ============================================================================

export const profitabilityInputSchema = z.object({
  startDate: z.string().date(), // YYYY-MM-DD (avoids timezone issues)
  endDate: z.string().date(),
  employeeIds: z.array(z.number()).optional(),
  teamIds: z.array(z.number()).optional(),
  maintenanceTaskIds: z.array(z.number()).optional(),
  customerIds: z.array(z.number()).optional(),
  customerTypes: z.array(z.string()).optional(),
})

export type ProfitabilityInput = z.infer<typeof profitabilityInputSchema>

// MRR belongs to customers, not to the people who worked a visit, so a view
// narrowed to people leaves it out.
export const includesMrr = (input: {
  employeeIds?: number[]
  teamIds?: number[]
}) => !input.employeeIds?.length && !input.teamIds?.length

// ============================================================================
// Summary (P&L overview)
// ============================================================================

export const profitabilitySummarySchema = z.object({
  taskRevenue: z.number(),
  lineItemRevenue: z.number(),
  subscriptionRevenue: z.number(),
  totalRevenue: z.number(),
  // Direct costs (allocated to visits)
  laborCost: z.number(),
  materialCost: z.number(),
  commissionCost: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  grossProfit: z.number(),
  grossMargin: z.number(), // 0-1
  // Full costs (real salary costs regardless of utilization)
  fullLaborCost: z.number(),
  fullTotalCost: z.number(),
  fullGrossProfit: z.number(),
  fullGrossMargin: z.number(), // 0-1
  visitCount: z.number(),
  revenuePerVisit: z.number(),
  costPerVisit: z.number(),
  profitPerVisit: z.number(),
  personDays: z.number(),
  people: z.number(),
  revenuePerPersonDay: z.number(), // work revenue only, no MRR
  revenuePerPersonMonth: z.number(), // per day × 21
})

export type ProfitabilitySummary = z.infer<typeof profitabilitySummarySchema>

// ============================================================================
// Monthly Trend
// ============================================================================

export const profitabilityMonthDataSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  revenue: z.number(),
  cost: z.number(),
  profit: z.number(),
  margin: z.number(),
  fullCost: z.number(),
  fullProfit: z.number(),
  fullMargin: z.number(),
})

export type ProfitabilityMonthData = z.infer<
  typeof profitabilityMonthDataSchema
>

// ============================================================================
// Visit Profitability
// ============================================================================

export const visitProfitabilityRowSchema = z.object({
  visitId: z.number(),
  // First and last booked day inside the period, then of the whole visit.
  date: z.string(),
  dateTo: z.string(),
  spanFrom: z.string(),
  spanTo: z.string(),
  customerName: z.string().nullable(),
  propertyAddress: z.string().nullable(),
  attendeeNames: z.array(z.string()),
  durationHours: z.number(),
  revenue: z.number(),
  laborCost: z.number(),
  materialCost: z.number(),
  commissionCost: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
})

export type VisitProfitabilityRow = z.infer<typeof visitProfitabilityRowSchema>

// ============================================================================
// Employee Profitability
// ============================================================================

export const employeeProfitabilityRowSchema = z.object({
  userId: z.number(),
  employeeId: z.number(),
  name: z.string(),
  role: z.string(),
  hoursWorked: z.number(),
  availableHours: z.number(),
  utilizationRate: z.number(), // 0-1
  visitCount: z.number(),
  revenue: z.number(),
  materialCost: z.number(),
  laborCost: z.number(),
  fullCost: z.number(),
  commissionPaid: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
  fullProfit: z.number(),
  fullMargin: z.number(),
  revenuePerHour: z.number(),
  costPerHour: z.number(),
})

export type EmployeeProfitabilityRow = z.infer<
  typeof employeeProfitabilityRowSchema
>

// ============================================================================
// Customer Profitability
// ============================================================================

export const customerProfitabilityRowSchema = z.object({
  customerId: z.number(),
  customerName: z.string(),
  customerType: z.string(),
  visitCount: z.number(),
  taskRevenue: z.number(),
  lineItemRevenue: z.number(),
  subscriptionRevenue: z.number(),
  totalRevenue: z.number(),
  laborCost: z.number(),
  materialCost: z.number(),
  commissionCost: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
})

export type CustomerProfitabilityRow = z.infer<
  typeof customerProfitabilityRowSchema
>

// ============================================================================
// Team Profitability (sum of the team's employee rows)
// ============================================================================

export const teamProfitabilityRowSchema = z.object({
  teamId: z.number().nullable(),
  teamName: z.string().nullable(),
  isSubcontractor: z.boolean(),
  people: z.number(),
  personDays: z.number(),
  utilizationRate: z.number(), // 0-1
  revenue: z.number(),
  laborCost: z.number(),
  materialCost: z.number(),
  commissionPaid: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
  fullCost: z.number(),
  fullProfit: z.number(),
  fullMargin: z.number(),
  revenuePerPersonDay: z.number(),
  revenuePerPersonMonth: z.number(),
})

export type TeamProfitabilityRow = z.infer<typeof teamProfitabilityRowSchema>

// ============================================================================
// Category Profitability (whole visits under their priciest task's category)
// ============================================================================

export const categoryProfitabilityRowSchema = z.object({
  category: z.string().nullable(),
  visitCount: z.number(),
  revenue: z.number(),
  lineItemRevenue: z.number(),
  laborCost: z.number(),
  materialCost: z.number(),
  commissionCost: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
  personDays: z.number(),
  revenuePerPersonDay: z.number(),
  revenuePerPersonMonth: z.number(),
})

export type CategoryProfitabilityRow = z.infer<
  typeof categoryProfitabilityRowSchema
>

// ============================================================================
// Service Profitability
// ============================================================================

export const serviceProfitabilityRowSchema = z.object({
  maintenanceTaskId: z.number(),
  taskName: z.string(),
  category: z.string().nullable(),
  count: z.number(),
  totalRevenue: z.number(),
  avgRevenue: z.number(),
  totalLaborCost: z.number(),
  avgLaborCost: z.number(),
  profit: z.number(),
  margin: z.number(),
})

export type ServiceProfitabilityRow = z.infer<
  typeof serviceProfitabilityRowSchema
>

// ============================================================================
// Service Monthly Trend
// ============================================================================

export const serviceMonthlyTrendRowSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  maintenanceTaskId: z.number(),
  taskName: z.string(),
  revenue: z.number(),
  laborCost: z.number(),
  profit: z.number(),
})

export type ServiceMonthlyTrendRow = z.infer<
  typeof serviceMonthlyTrendRowSchema
>

// ============================================================================
// Segment Monthly Trend
// ============================================================================

export const segmentMonthlyTrendRowSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  customerType: z.string(),
  revenue: z.number(),
  laborCost: z.number(),
  profit: z.number(),
})

export type SegmentMonthlyTrendRow = z.infer<
  typeof segmentMonthlyTrendRowSchema
>

// ============================================================================
// Response schemas with totals
// ============================================================================

export const profitabilityTotalsSchema = z.object({
  revenue: z.number(),
  laborCost: z.number(),
  materialCost: z.number(),
  commissionCost: z.number(),
  dailyAllowanceCost: z.number(),
  totalCost: z.number(),
  profit: z.number(),
  margin: z.number(),
})

export type ProfitabilityTotals = z.infer<typeof profitabilityTotalsSchema>
